import React, { useState, useEffect, useMemo } from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import Icon from '@material-ui/core/Icon';
import TariffHelper, { TariffType, ISPUTariff, ILPUTariff } from '../../../../@types/model/transformer/tariffs/tariffs';
import LPUEdit from '../LPUEdit';
import SPUEdit from '../SPUEdit';
import { FirebaseService } from '../../../../services/firebaseService';
import { TRANSMISSION_ZONES, LPU_VOLTAGES, LPU_CATEGORIES } from '../../../../appConstants';
import moment from 'moment';
import generalFunctions from '../../../../store/general/functions';
import Fab from '@material-ui/core/Fab';
import { connect, useSelector } from 'react-redux';
import { IRootState } from '../../../../@types/redux';
import { IValueAddedTax } from '../../../../@types/model/masterData/valueAddedTax/valueAddedTax';
import MasterDataValueAddedTaxFunctions from '../../../../store/masterData/valueAddedTax/functions';
import { EmployeeHelper } from '../../../../@types/employee';
import firebase from 'firebase';
import ProjectDialog from '../../../customComponentV2/dialog/ProjectDialog';

interface ITariffEditDialogProps {
    tariffType : TariffType;

    isNew : boolean;
    enableEdit ?: boolean;
    valueAddedTaxes : Array<IValueAddedTax>;

    /**
     * Document Id of tariff
     */
    tariffId ?: string;
}

const TariffEditDialogComponent = (props : ITariffEditDialogProps) : React.ReactElement => {

    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [lpuTariff, setLpuTariff] = useState<ILPUTariff | null>(null);
    const [spuTariff, setSpuTariff] = useState<ISPUTariff | null>(null);
    const [vat, setVat] = useState<number>(0);

    useEffect(() => {
        MasterDataValueAddedTaxFunctions.load();
    }, []);

    const auth = useSelector((state : IRootState) => state.auth);

    const loadLPU = async () => {
        setIsLoading(true);

        try {
            if (props.isNew) {
                if (!auth.session?.employee) {
                    return;
                }

                const lpuTariff : ILPUTariff = {
                    startDate: moment.utc().startOf('day').valueOf(),
                    endDate: null,
                    type: 'lpu',
                    zones: TRANSMISSION_ZONES.map(n => ({
                        lower: n[0],
                        upper: n[1],
                        highDemand: { offPeak: [0, 0], peak: [0, 0], standard: [0, 0] },
                        lowDemand: { offPeak: [0, 0], peak: [0, 0], standard: [0, 0] },
                        highDemandMonths: [6, 7, 8],
                        lowDemandMonths: [1, 2, 3, 4, 5, 9, 10, 11, 12],
                        netwrokCapacity: [0, 0],
                    })),
                    voltages: LPU_VOLTAGES.map(n => ({
                        lower: n[0],
                        upper: n[1],
                        ancillaryServiceCharge: 0,
                        networkDemandCharge: 0,
                    })),
                    categories: LPU_CATEGORIES.map(n => ({
                        administrationCharge: 0,
                        lower: n[0],
                        upper: n[1],
                        serviceCharge: 0,
                    })),
                    reactiveEnergyCharge: { high: 0, low: 0 },
                    vat: 0,
                    ref: TariffHelper.lpuDoc(),
                    createdBy: EmployeeHelper.collection().doc(auth.session.employee.Id),
                    createdByEmployee: auth.session.employee.EmployeeNumber ?? '',
                    createdByName: auth.session.employee.Name,
                    createdOn: firebase.firestore.Timestamp.now(),
                    updatedBy: EmployeeHelper.collection().doc(auth.session.employee.Id),
                    updatedByEmployee: auth.session.employee.EmployeeNumber ?? '',
                    updatedByName: auth.session.employee.Name,
                    updatedOn: firebase.firestore.Timestamp.now(),
                };
                
                setLpuTariff(lpuTariff);
            } else {
                const lpuSnapshot = (await TariffHelper.lpuDoc(props.tariffId).get()).data() ?? null;
                setLpuTariff(lpuSnapshot);
            }
        } catch (ex) {
            generalFunctions.generalShowError(ex, 'Error loading LPU tariff');
        } finally {
            setIsLoading(false);
        }
    };

    const loadSPU = async () => {
        setIsLoading(true);

        try {
            if (props.isNew) {
                const previousSpuTariff = props.tariffId 
                    ? (await TariffHelper.spuDoc(props.tariffId).get()).data()
                    : null;

                const prevLandrate = previousSpuTariff?.landrates;

                if (!auth.session?.employee) {
                    return;
                }

                const spuTariff : ISPUTariff = {
                    ref: TariffHelper.spuDoc(),
                    type: 'spu',
                    startDate: moment.utc().startOf('day').valueOf(),
                    endDate: null,
                    vat: 0,
                    landrates: {
                        1: { 
                            energyCharge: prevLandrate?.[1].energyCharge ?? 0, 
                            ancillaryService: prevLandrate?.[1].ancillaryService ?? 0, 
                            networkDemand: prevLandrate?.[1].networkDemand ?? 0,
                            networkCapacity: prevLandrate?.[1].networkCapacity ?? 0,
                            serviceCharge: prevLandrate?.[1].serviceCharge ?? 0,
                            generationCapacityCharge: prevLandrate?.[1].generationCapacityCharge ?? 0,
                        },
                        2: { 
                            energyCharge: prevLandrate?.[2].energyCharge ?? 0, 
                            ancillaryService: prevLandrate?.[2].ancillaryService ?? 0, 
                            networkDemand: prevLandrate?.[2].networkDemand ?? 0,
                            networkCapacity: prevLandrate?.[2].networkCapacity ?? 0,
                            serviceCharge: prevLandrate?.[2].serviceCharge ?? 0,
                            generationCapacityCharge: prevLandrate?.[2].generationCapacityCharge ?? 0,
                        },
                        3: { 
                            energyCharge: prevLandrate?.[3].energyCharge ?? 0, 
                            ancillaryService: prevLandrate?.[3].ancillaryService ?? 0, 
                            networkDemand: prevLandrate?.[3].networkDemand ?? 0,
                            networkCapacity: prevLandrate?.[3].networkCapacity ?? 0,
                            serviceCharge: prevLandrate?.[3].serviceCharge ?? 0,
                            generationCapacityCharge: prevLandrate?.[3].generationCapacityCharge ?? 0,
                        },
                        4: { 
                            energyCharge: prevLandrate?.[4].energyCharge ?? 0, 
                            ancillaryService: prevLandrate?.[4].ancillaryService ?? 0, 
                            networkDemand: prevLandrate?.[4].networkDemand ?? 0,
                            networkCapacity: prevLandrate?.[4].networkCapacity ?? 0,
                            serviceCharge: null,
                            generationCapacityCharge: prevLandrate?.[4].generationCapacityCharge ?? 0, 
                        },
                        DX: { 
                            energyCharge: null, 
                            ancillaryService: null, 
                            networkDemand: null,
                            networkCapacity: null,
                            serviceCharge: prevLandrate?.DX.serviceCharge ?? 0,
                            generationCapacityCharge: null, 
                        },
                        // Landlight 20A
                        5: {
                            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                            energyCharge: prevLandrate?.[5]?.energyCharge ?? 0, 
                            ancillaryService: null, 
                            networkDemand: null,
                            networkCapacity: null,
                            serviceCharge: null,
                            generationCapacityCharge: null, 
                        },
                        // Landlight 60A
                        6: {
                            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                            energyCharge: prevLandrate?.[6]?.energyCharge ?? 0, 
                            ancillaryService: null, 
                            networkDemand: null,
                            networkCapacity: null,
                            serviceCharge: null,
                            generationCapacityCharge: null, 
                        },
                    },
                    createdBy: EmployeeHelper.collection().doc(auth.session.employee.Id),
                    createdByEmployee: auth.session.employee.EmployeeNumber ?? '',
                    createdByName: auth.session.employee.Name,
                    createdOn: firebase.firestore.Timestamp.now(),
                    updatedBy: EmployeeHelper.collection().doc(auth.session.employee.Id),
                    updatedByEmployee: auth.session.employee.EmployeeNumber ?? '',
                    updatedByName: auth.session.employee.Name,
                    updatedOn: firebase.firestore.Timestamp.now(),
                };

                setSpuTariff(spuTariff);
            } else {
                const spuTariff = props.tariffId 
                    ? (await TariffHelper.spuDoc(props.tariffId).get()).data()
                    : null;

                if (!auth.session?.employee || !spuTariff) {
                    return;
                }

                const spuTariffToEdit : ISPUTariff = {
                    ...spuTariff,
                    landrates: {
                        1: { 
                            energyCharge: spuTariff.landrates[1].energyCharge ?? 0, 
                            ancillaryService: spuTariff.landrates[1].ancillaryService ?? 0, 
                            networkDemand: spuTariff.landrates[1].networkDemand ?? 0,
                            networkCapacity: spuTariff.landrates[1].networkCapacity ?? 0,
                            serviceCharge: spuTariff.landrates[1].serviceCharge ?? 0,
                            generationCapacityCharge: spuTariff.landrates[1].generationCapacityCharge ?? 0,
                        },
                        2: { 
                            energyCharge: spuTariff.landrates[2].energyCharge ?? 0, 
                            ancillaryService: spuTariff.landrates[2].ancillaryService ?? 0, 
                            networkDemand: spuTariff.landrates[2].networkDemand ?? 0,
                            networkCapacity: spuTariff.landrates[2].networkCapacity ?? 0,
                            serviceCharge: spuTariff.landrates[2].serviceCharge ?? 0,
                            generationCapacityCharge: spuTariff.landrates[2].generationCapacityCharge ?? 0,
                        },
                        3: { 
                            energyCharge: spuTariff.landrates[3].energyCharge ?? 0, 
                            ancillaryService: spuTariff.landrates[3].ancillaryService ?? 0, 
                            networkDemand: spuTariff.landrates[3].networkDemand ?? 0,
                            networkCapacity: spuTariff.landrates[3].networkCapacity ?? 0,
                            serviceCharge: spuTariff.landrates[3].serviceCharge ?? 0,
                            generationCapacityCharge: spuTariff.landrates[3].generationCapacityCharge ?? 0,
                        },
                        4: { 
                            energyCharge: spuTariff.landrates[4].energyCharge ?? 0, 
                            ancillaryService: spuTariff.landrates[4].ancillaryService ?? 0, 
                            networkDemand: spuTariff.landrates[4].networkDemand ?? 0,
                            networkCapacity: spuTariff.landrates[4].networkCapacity ?? 0,
                            serviceCharge: null,
                            generationCapacityCharge: spuTariff.landrates[4].generationCapacityCharge ?? 0, 
                        },
                        DX: { 
                            energyCharge: null, 
                            ancillaryService: null, 
                            networkDemand: null,
                            networkCapacity: null,
                            serviceCharge: spuTariff.landrates.DX.serviceCharge ?? 0,
                            generationCapacityCharge: null, 
                        },
                        // Landlight 20A
                        5: {
                            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                            energyCharge: spuTariff.landrates?.[5]?.energyCharge ?? 0, 
                            ancillaryService: null, 
                            networkDemand: null,
                            networkCapacity: null,
                            serviceCharge: null,
                            generationCapacityCharge: null, 
                        },
                        // Landlight 60A
                        6: {
                            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                            energyCharge: spuTariff.landrates?.[6]?.energyCharge ?? 0,
                            ancillaryService: null, 
                            networkDemand: null,
                            networkCapacity: null,
                            serviceCharge: null,
                            generationCapacityCharge: null, 
                        },
                    },
                };

                setSpuTariff(spuTariffToEdit);
            }
        } catch (ex) {
            generalFunctions.generalShowError(ex, 'Error loading SPU tariff');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            if (props.tariffType === 'lpu') {
                setSpuTariff(null);
                loadLPU();
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            } else if (props.tariffType === 'spu') {
                setLpuTariff(null);
                loadSPU();
            }
        }
    }, [isOpen, props.tariffType]);

    useEffect(() => {
        if (!props.valueAddedTaxes.length) {
            return;
        }

        if (lpuTariff) {
            const vatDate = lpuTariff.startDate
                ? moment(lpuTariff.startDate)
                : moment();

            const vat = FirebaseService.getVatByDay(vatDate, props.valueAddedTaxes);
            setVat(vat);
        }
    }, [lpuTariff, props.valueAddedTaxes]);

    const handleClickOpen = () => {
        setIsOpen(true);
    };

    const handleClose = () => {
        setIsOpen(false);
        setLpuTariff(null);
        setSpuTariff(null);
    };

    const handleSaved = () => {
        setIsOpen(false);
    };

    const tooltipText = useMemo(() => {
        if (props.isNew) return 'Add';
        if (props.enableEdit) return 'Edit';
        return 'View';
    }, [props.isNew, props.enableEdit]);

    return (
        <>
            <Tooltip title={tooltipText}>
                <div>
                    {props.isNew ? (
                        <Fab size='small' color='secondary' className='bcy' aria-label='Add' onClick={handleClickOpen}>
                            <Icon>add</Icon>
                        </Fab>
                    ) : (
                        <Fab size='small' color='secondary' className='bcy' variant='extended' aria-label='View' onClick={handleClickOpen}>
                            <span className='pl10 pr10'>{tooltipText}</span>
                        </Fab>
                    )}
                </div>
            </Tooltip>
            <ProjectDialog
                isOpen={isOpen}
                onClose={handleClose}
                fullScreen
                title={`${props.isNew ? 'Create' : tooltipText} ${props.tariffType.toLocaleUpperCase()} Tariffs`}
                isLoadingCircular={isLoading}
            >
                {
                    lpuTariff && 
                    <LPUEdit 
                        lpu={lpuTariff} 
                        vat={vat} 
                        onSaved={handleSaved} 
                        enabled={props.isNew || !lpuTariff.endDate} 
                        disabledStartDate={!!props.enableEdit} 
                    />
                }
                {
                    spuTariff && 
                    <SPUEdit 
                        spu={spuTariff} 
                        onSaved={handleSaved} 
                        enabled={props.isNew || !spuTariff.endDate} 
                        disabledStartDate={props.enableEdit} 
                    />
                }
            </ProjectDialog>
        </>
    );
};

const mapStateToProps = (state : IRootState) => {
    return {
        valueAddedTaxes: state.masterData.valueAddedTax.valueAddedTaxes,
    };
};

const TariffEditDialog = connect(
    mapStateToProps,
)(TariffEditDialogComponent);

export default TariffEditDialog;
